import React, { useState } from 'react';

import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TROMBI_TYPES = [
  { label: 'Trombinoscope SIMPLE (nom + prénom + photo)', value: 1 },
  { label: 'Trombinoscope COMPLET (nom + prénom + citation + photo)', value: 2 },
];

interface AddSectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (section: { id: number; name: string; trombi_type: number; all_users_count?: number }) => void;
}

const AddSectionDialog: React.FC<AddSectionDialogProps> = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [trombiType, setTrombiType] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/sections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section: { name: name.trim(), trombi_type: Number(trombiType) } }),
      });
      if (!res.ok) throw new Error('Erreur');
      const data = await res.json().catch(() => ({}));
      onCreated({
        id: data.id ?? Date.now(),
        name: data.name ?? name.trim(),
        trombi_type: data.trombi_type ?? Number(trombiType),
        all_users_count: data.all_users_count ?? 0,
      });
      setName('');
      setTrombiType('1');
      onOpenChange(false);
    } catch (err) {
      setError("Impossible de créer la classe.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter une classe</DialogTitle>
          <DialogDescription>Créez une nouvelle classe pour le trombinoscope.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="section-name">Nom de la classe</Label>
            <Input
              id="section-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex : CM2 - Mme Dupont"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="section-type">Type de trombinoscope</Label>
            <Select value={trombiType} onValueChange={setTrombiType}>
              <SelectTrigger id="section-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TROMBI_TYPES.map((t) => (
                  <SelectItem key={t.value} value={String(t.value)}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={!name.trim() || saving}>
              {saving && <Loader2 size={14} className="animate-spin mr-1" />}
              Créer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSectionDialog;
